import { useEffect, useRef, useState } from 'react';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Input, message, Popover, Radio, Select, Space, Switch, Tag } from 'antd';
import { twMerge } from 'tailwind-merge';
import { AUTO_LIGHT_TYPES } from '../../../../Article/Documents/const';
import { useDevice } from '../../../../hooks/useDevice';
import { MODEL } from '../../api';
import {
    clampWordCount,
    DEFAULT_CONTENTS,
    formatWordCount,
    isSpecialQueryType,
    normalizeQueryType,
    wordCountOptionsOf,
    WRITING_TYPE_OPTIONS,
} from '../utils';
import '../../../../Article/Documents/GraduationThesisSteps/styles/GraduationThesisStep1.scss';
import styles from './index.module.scss';

const MAX_TITLE_LENGTH = 120;

/** 原首页的元素标签（表 / 图 / 数据 / 公式 / 代码）→ 新流程的元素 key */
const LIGHT_TYPE_TO_CONTENT = {
    TABLE: 'table',
    DIAGRAM: 'image',
    DATA: 'data',
    FORMULA: 'formula',
    CODE: 'code',
};

/** 手机上元素标签用双字文案（桌面保持常量里的「表」「图」） */
const MOBILE_LIGHT_TYPE_LABEL = {
    TABLE: '表格',
    DIAGRAM: '图片',
};

const MOBILE_TITLE_PLACEHOLDER = '请输入文章题目，例如：基于深度学习的宿舍管理系统设计与实现';

// ==================== 主组件 ====================

/**
 * 第一步：需求配置。外观沿用原来灵感风暴首页的表单：
 * 题目输入（左侧写作类型）、元素标签、字数、模型、「免费解锁」按钮、三级大纲开关。
 * 参考资料在下一步单独选择。
 */
export default function ConfigurationStep({
    title,
    initialTitle,
    initialServiceType,
    initialWordCount,
    initialContents,
    initialContentsTouched = false,
    initialEnableLevel3 = false,
    model,
    onModelChange,
    onTitleChange,
    onServiceTypeChange,
    onNext,
}) {
    const { isMobile } = useDevice();

    // ===== 写作类型 / 生成参数 =====
    const [serviceType, setServiceType] = useState(() => normalizeQueryType(initialServiceType));
    const [wordCount, setWordCount] = useState(() =>
        clampWordCount(normalizeQueryType(initialServiceType), initialWordCount),
    );
    // 每种写作类型上次选的篇幅：切走再切回来时恢复
    const wordCountByTypeRef = useRef({});
    const contentsTouchedRef = useRef(Boolean(initialContentsTouched));
    const [contents, setContents] = useState(() =>
        initialContentsTouched
            ? initialContents || []
            : isSpecialQueryType(initialServiceType)
              ? []
              : initialContents || DEFAULT_CONTENTS,
    );
    const [enableLevel3, setEnableLevel3] = useState(Boolean(initialEnableLevel3));
    const isLevel3Required = wordCount >= 30000;
    const isPremium = model === MODEL.ACADEMIC;
    const wordCountOptions = wordCountOptionsOf(serviceType);

    useEffect(() => {
        if (isLevel3Required) setEnableLevel3(true);
    }, [isLevel3Required]);

    const handleSelectWritingType = (next) => {
        if (next === serviceType) return;
        wordCountByTypeRef.current[serviceType] = wordCount;
        setServiceType(next);
        setWordCount(clampWordCount(next, wordCountByTypeRef.current[next]));
        if (!contentsTouchedRef.current) {
            setContents(isSpecialQueryType(next) ? [] : DEFAULT_CONTENTS);
        }
    };

    // 首页 Tab 切换写作类型时同步过来；已填的标题等保留
    useEffect(() => {
        handleSelectWritingType(normalizeQueryType(initialServiceType));
    }, [initialServiceType]);

    const toggleContent = (key) => {
        contentsTouchedRef.current = true;
        setContents((prev) =>
            prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key],
        );
    };

    // ===== 标题 =====
    const [docTitle, setDocTitle] = useState(initialTitle || title || '');
    const titleInputRef = useRef(null);

    useEffect(() => {
        onTitleChange?.(docTitle.trim());
    }, [docTitle, onTitleChange]);

    // ===== 提交 =====
    const handleNext = () => {
        const finalTitle = docTitle.replace(/\s+/g, ' ').trim();
        if (!finalTitle) {
            message.warning('请输入文章题目后再继续');
            titleInputRef.current?.focus?.();
            return;
        }
        onNext({
            title: finalTitle,
            serviceType,
            wordCount,
            contents,
            contentsTouched: contentsTouchedRef.current,
            enableLevel3: isLevel3Required || enableLevel3,
        });
    };

    return (
        <div className={twMerge('graduation-thesis-step1 w-full pb-2 pt-2', styles.step)}>
            <div className="flex h-max w-full flex-col items-start rounded-lg">
                {/* 题目：桌面是「写作类型下拉 + 单行题目」左右拼接；
                    手机只有一个多行大文本框（类型由顶部的分段页签切换，同步到 serviceType） */}
                {isMobile ? (
                    <div className={twMerge('w-full', styles.titleField)}>
                        <Input.TextArea
                            ref={titleInputRef}
                            aria-label="文档标题"
                            placeholder={MOBILE_TITLE_PLACEHOLDER}
                            size="large"
                            autoSize={{ minRows: 3, maxRows: 6 }}
                            maxLength={MAX_TITLE_LENGTH}
                            value={docTitle}
                            onChange={(e) => setDocTitle(e.target.value.replace(/\r?\n/g, ''))}
                            onPressEnter={(e) => {
                                e.preventDefault();
                                handleNext();
                            }}
                        />
                    </div>
                ) : (
                    <Space.Compact className="w-full">
                        <Select
                            size="large"
                            className={styles.typeSelect}
                            aria-label="写作类型"
                            value={serviceType}
                            options={WRITING_TYPE_OPTIONS}
                            popupMatchSelectWidth={false}
                            onChange={(value) => {
                                handleSelectWritingType(value);
                                onServiceTypeChange?.(value);
                            }}
                            style={{ width: 150 }}
                        />
                        <Input
                            ref={titleInputRef}
                            aria-label="文档标题"
                            placeholder="请输入文章题目"
                            size="large"
                            style={{ width: '100%' }}
                            maxLength={MAX_TITLE_LENGTH}
                            value={docTitle}
                            onChange={(e) => setDocTitle(e.target.value.replace(/\r?\n/g, ''))}
                            onPressEnter={handleNext}
                        />
                    </Space.Compact>
                )}

                {/* 手机：元素标签上方加一个「包含内容」小标题，与下面的「字数」「模型」同一套层级 */}
                {isMobile && (
                    <span
                        className={twMerge(
                            'text-nowrap text-[#192038]',
                            styles.fieldLabel,
                            styles.tagsLabel,
                        )}
                    >
                        包含内容：
                    </span>
                )}

                {/* 包含元素：表 / 图 / 数据 / 公式 / 代码 */}
                <div
                    className={twMerge(
                        'mt-5 flex flex-wrap gap-2.5 md:mt-8 md:gap-3',
                        styles.elementTags,
                    )}
                >
                    {AUTO_LIGHT_TYPES.map(({ value, label, icon, activeIcon, activeUltraIcon }) => {
                        const key = LIGHT_TYPE_TO_CONTENT[value];
                        if (!key) return null;
                        const isActive = contents.includes(key);
                        const text = isMobile ? MOBILE_LIGHT_TYPE_LABEL[value] || label : label;
                        return (
                            <Tag
                                key={value}
                                onClick={() => toggleContent(key)}
                                className={twMerge(
                                    'flex min-h-[36px] cursor-pointer items-center gap-1 rounded-md bg-[#F8FBFF] px-3 py-1 md:min-h-0 md:px-5',
                                    isActive && 'tag-primary-bg',
                                    styles.elementTag,
                                )}
                            >
                                <img
                                    src={
                                        isActive ? (isPremium ? activeUltraIcon : activeIcon) : icon
                                    }
                                    alt={text}
                                    className="h-4 w-4"
                                />
                                <span className={twMerge('text-sm', isActive && 'text-primary')}>
                                    {text}
                                </span>
                            </Tag>
                        );
                    })}
                </div>
            </div>

            {/* 字数 + 模型 */}
            <div
                className={twMerge(
                    'mb-6 mt-6 flex w-full flex-col justify-start gap-5 md:mb-8 md:mt-10 md:gap-6',
                    styles.paramsBlock,
                )}
            >
                <div
                    className={twMerge(
                        'flex items-start gap-3 md:items-center md:gap-4',
                        styles.fieldRow,
                    )}
                >
                    <span
                        className={twMerge(
                            'text-nowrap leading-6 text-[#192038]',
                            styles.fieldLabel,
                        )}
                    >
                        字数：
                    </span>
                    <div
                        className={twMerge(
                            'flex flex-wrap gap-x-4 gap-y-2',
                            styles.wordCountOptions,
                        )}
                        role="radiogroup"
                        aria-label="文档篇幅"
                    >
                        {wordCountOptions.map((count) => (
                            <span
                                key={count}
                                role="radio"
                                aria-checked={wordCount === count}
                                onClick={() => setWordCount(count)}
                                className={`cursor-pointer text-base leading-6 ${
                                    wordCount === count
                                        ? 'font-bold text-primary underline'
                                        : 'text-[#414C6F] hover:text-primary'
                                } ${styles.wordCountOption}`}
                            >
                                {/* 手机显示「3000字 / 1万字 / 1.5万字」，值不变；桌面保持数字 */}
                                {isMobile ? formatWordCount(count) : count}
                            </span>
                        ))}
                    </div>
                </div>

                <div
                    className={twMerge(
                        'flex items-start gap-3 md:items-center md:gap-4',
                        styles.fieldRow,
                    )}
                >
                    <span
                        className={twMerge(
                            'text-nowrap leading-6 text-[#192038]',
                            styles.fieldLabel,
                        )}
                    >
                        模型：
                    </span>
                    <Radio.Group
                        className={styles.modelGroup}
                        value={isPremium ? MODEL.ACADEMIC : MODEL.NORMAL}
                        onChange={(e) => onModelChange?.(e.target.value)}
                    >
                        <Radio value={MODEL.NORMAL}>
                            <span className={styles.modelTitle}>学术模型</span>
                            {/* 手机上两个模型都是「标题 + 一行说明」的卡片；桌面保持原样 */}
                            {isMobile && (
                                <span className={styles.modelDesc}>(标准生成，速度更快)</span>
                            )}
                        </Radio>
                        <Radio value={MODEL.ACADEMIC} className={styles.premiumRadio}>
                            {/* 星标与文字不换行拆开：手机上窄时只有说明文字换到下一行 */}
                            <div className="flex items-start gap-0.5">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="#8b5cf6"
                                    className="h-4 w-4 flex-shrink-0 translate-y-[3px]"
                                    aria-hidden
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M9 4.5a.75.75 0 01.721.544l.813 2.846a3.75 3.75 0 002.576 2.576l2.846.813a.75.75 0 010 1.442l-2.846.813a3.75 3.75 0 00-2.576 2.576l-.813 2.846a.75.75 0 01-1.442 0l-.813-2.846a3.75 3.75 0 00-2.576-2.576l-2.846-.813a.75.75 0 010-1.442l2.846-.813a3.75 3.75 0 002.576-2.576l.813-2.846A.75.75 0 019 4.5z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                                <div
                                    className={twMerge(
                                        'flex min-w-0 flex-wrap gap-1',
                                        styles.premiumLabel,
                                    )}
                                >
                                    <span
                                        className={twMerge(
                                            'bg-gradient-to-r from-[#8b5cf6] to-[#6d28d9] bg-clip-text font-semibold text-transparent',
                                            styles.premiumTitle,
                                        )}
                                    >
                                        超级自研学术特调模型
                                    </span>
                                    <span
                                        className={twMerge(
                                            'translate-y-[3px] text-xs font-normal text-gray-500',
                                            styles.premiumDesc,
                                        )}
                                    >
                                        (深度思考，AI 生成配图)
                                    </span>
                                </div>
                            </div>
                        </Radio>
                    </Radio.Group>
                </div>
            </div>

            {/* 免费解锁 */}
            <div className={twMerge('mt-6 flex justify-center md:mt-12', styles.unlockRow)}>
                <button
                    type="button"
                    className="unlock-button"
                    data-track="config_next"
                    onClick={handleNext}
                >
                    免费解锁
                </button>
            </div>

            {/* 三级大纲 */}
            <div
                className={twMerge(
                    'mt-5 flex min-h-[44px] items-center justify-center md:mt-6 md:min-h-0',
                    styles.level3Row,
                )}
            >
                <Switch
                    checked={isLevel3Required || enableLevel3}
                    disabled={isLevel3Required}
                    onChange={(checked) => setEnableLevel3(checked)}
                />
                <div className="ml-1 flex items-center">
                    使用三级大纲
                    <Popover
                        trigger={isMobile ? 'click' : 'hover'}
                        content={
                            <div className="max-w-[300px] text-sm">
                                <div className="mb-2 font-medium">三级大纲说明：</div>
                                <div className="text-gray-600">
                                    • 二级大纲：极速简约，结构清晰
                                    <br />
                                    • 三级大纲：内容丰富，深度思考
                                    <br />
                                    <br />
                                    选择三级大纲可以获得更详细的长文结构规划，但生成时间会相应增加。
                                    3 万字及以上会自动使用三级大纲。
                                </div>
                            </div>
                        }
                    >
                        <QuestionCircleOutlined className="ml-1" />
                    </Popover>
                </div>
            </div>
        </div>
    );
}
